<template>
  <div class="video-level">
    <LevelRow
      :hasAccess="hasAccess || isVideoFree()"
      :contentType="contentType"
      :contentId="video.id"
      :status="videoStatus"
      :statusText="statusText"
      :parentStatus="parentStatus"
      :title="title"
      :plansLink="plansLink"
      :isWatching="isWatching"
      :details="videoDetails"
      :progress="progress"
      @toggle="toggleVideo"
      @click.native="toggleVideo"
    >
    </LevelRow>
    <div v-if="isWatching" class="video-level__content">
      <div class="video-level__content__video">
        <ContentVideo
          :video="video"
          :isWatching="isWatching"
          :quizId="quiz && quiz.id ? quiz.id : ''"
          :hasPassedQuiz="isQuizFinished(quizStatus)"
          :nextVideo="nextVideo"
          :videoStatus="videoStatus"
          @is-loading-video="setLoader"
          @toggle-video="$emit('toggle-video')"
          @toggle-watched="$emit('toggle-watched')"
        />
        <p class="video-level__content__video__footer">
          Not seeing the closed captioning language you’re looking for?
          <span
            class="video-level__content__video__footer__btn"
            @click="toggleReqLang"
          >
            Request new language
          </span>
        </p>
      </div>
    </div>
    <SlotedModal v-if="showReqLang" @close-modal="toggleReqLang">
      <div class="video-level__req-lang-container">
        <template v-if="langReqStep === 1">
          <h2 class="video-level__req-lang-container__title alert__title">
            Request new closed captioning language
          </h2>
          <p class="video-level__req-lang-container__text alert__subtitle">
            Missing a closed captioning language? We've got you covered! Request
            a new language, and we'll add it to our system.
          </p>
          <Dropdown
            class="video-level__req-lang-container__dropdown"
            :options="languageOptions"
            :handleSelection="dropdownHandler"
          />
        </template>
        <template v-if="langReqStep === 2">
          <h2 class="video-level__req-lang-container__title alert__title">
            Request processing
          </h2>
          <p class="video-level__req-lang-container__text alert__subtitle">
            After a few minutes, refresh the page and you'll find the requested
            language available. We appreciate your input as we strive to make
            our content accessible to all.
          </p>
        </template>
        <div class="video-level__req-lang-container__buttons-wrapper">
          <button
            v-if="langReqStep === 1"
            class="btn"
            :disabled="!selectedLanguage"
            @click="sendLanguageRequest"
          >
            Request new language
          </button>
          <button
            :class="['btn', langReqStep === 1 && 'btn--secondary']"
            @click="toggleReqLang"
          >
            Close
          </button>
        </div>
      </div>
    </SlotedModal>
  </div>
</template>

<script>
import { isFreeContent } from '@/helpers'
import { mapState, mapGetters, mapActions } from 'vuex'
import { intervalToDuration, addSeconds } from 'date-fns'
import CONSTANTS from '@/constants'
import LevelRow from '@/components/content/level/LevelRow.vue'
import ContentVideo from '@/components/content/level/CourseVideo.vue'
import SlotedModal from '@/components/certifications/AlertModal.vue'
import Dropdown from '@/components/forms/elements/Dropdown.vue'

export default {
  name: 'VideoLevel',
  components: {
    LevelRow,
    Dropdown,
    ContentVideo,
    SlotedModal,
  },
  props: {
    parentStatus: {
      type: String,
      required: true,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    nextVideo: {
      type: Object,
      required: true,
    },
    plansLink: {
      type: String,
      required: true,
    },
    quiz: {
      type: Object,
      required: false,
    },
    quizStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    video: {
      type: Object,
      required: true,
    },
    videoStatus: {
      type: String,
      required: true,
    },
    instructorName: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isLoading: false,
      showReqLang: false,
      langReqStep: 1,
      languageOptions: CONSTANTS.LANGUAGE_CODES,
      selectedLanguage: null,
    }
  },
  computed: {
    ...mapState({
      playingVideoId: state => state.content.playingVideoId,
      currentHoveredNote: state => state.content.currentHoveredNote,
      currentCourseId: state => state.courses.currentCourse.id,
    }),
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    isViewingLevelNote() {
      if (this.levelBookmarks && this.levelBookmarks.length) {
        const ids = this.levelBookmarks.map(bookmark => bookmark.video)
        return ids.includes(this.currentHoveredNote)
      }
      return false
    },
    isWatching() {
      return this.video.id === this.playingVideoId
    },
    levelBookmarks() {
      return this.courseBookmarks.filter(
        bookmark =>
          bookmark.video === this.video.id &&
          bookmark.metadata.course_id === this.currentCourseId
      )
    },
    contentType() {
      return CONSTANTS.CONTENT_TYPES.VIDEO
    },
    progress() {
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        return 50
      }
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        return 100
      }
      return 0
    },
    statusText() {
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        return `Resume video`
      }
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        return `Restart video`
      }
      return `Start video`
    },
    videoDetails() {
      const details = this.instructorName
        ? [this.instructorName, this.getDuration()]
        : [this.getDuration()]
      return details
    },
  },
  methods: {
    ...mapActions({
      postVideoLanguageRequest: 'content/postVideoLanguageRequest',
    }),
    dropdownHandler(option) {
      this.selectedLanguage = option.value
    },
    async sendLanguageRequest() {
      const { video, selectedLanguage } = this
      const { parent_id } = video
      const success = await this.postVideoLanguageRequest({
        course_id: parent_id,
        language: selectedLanguage,
      })

      if (success) this.langReqStep = 2
    },
    toggleReqLang() {
      this.langReqStep = 1
      this.selectedLanguage = null
      this.showReqLang = !this.showReqLang
    },
    async toggleVideo() {
      if (this.hasAccess && !this.isLoading) {
        this.isLoading = true
        const { playingVideoId, video } = this
        if (playingVideoId === video.id) {
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setPlayingVideoTime', 0)
        } else {
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', video.id)
          this.$emit('toggle-content', CONSTANTS.CONTENT_TYPES.VIDEO)
        }
        this.isLoading = false
      }
    },
    toggleNextContent() {
      const { nextVideo } = this
      if (nextVideo) {
        this.$store.dispatch('content/setPlayingVideoId', nextVideo.uuid)
        this.$store.dispatch('content/setPlayingVideoTime', 0)
      }
    },
    isVideoFree() {
      return isFreeContent(this.video)
    },
    setLoader(state) {
      this.isLoading = state
    },
    isQuizFinished(status) {
      return CONSTANTS.CONTENT_USER_STATUS.FINISHED === status
    },
    getDuration() {
      return this.$duration.durationFromSecs(
        this.video?.duration_in_seconds || 0,
        'seconds'
      )
    },
  },
}
</script>

<style scoped lang="scss">
.video-level {
  &__content {
    &__video {
      &__footer {
        color: var(--white);
        text-align: right;
        margin-top: 15px;

        font-size: rem(12px);
        font-family: var(--font-book);
        font-weight: 450;
        line-height: rem(14px);
        &__btn {
          margin-left: 5px;
          color: var(--purple-pale);
          text-align: right;
          cursor: pointer;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
  &__req-lang-container {
    max-width: 530px;

    &__title {
      margin: 50px auto 20px;
    }
    &__text {
      margin-bottom: 20px;
    }
    &__dropdown {
      --vs-dropdown-max-height: 200px;

      margin: auto;
      max-width: 300px;
    }
    &__buttons-wrapper {
      display: flex;
      flex-flow: column;
      align-items: center;
      gap: 20px;
      margin: 40px auto 50px;
    }
  }
}
</style>
